import type { KUTCResultEntry } from '../services/kutcResultsService';
import { isoToIoc } from '../constants/iocCountryCodes';

export interface ExportIdentity {
  gender?: unknown;
  dateOfBirth?: unknown;
  nationality?: unknown;
  error?: string;
}

export interface ExportResultRow {
  personId: number;
  rank: number | 'DNF' | 'DNS' | 'DSQ' | '';
  time: string;
  firstName: string;
  lastName: string;
  gender: string;
  birthdate: string;
  nationality: string;
  bib: string;
  status: string;
  issues: string[];
  identityIssues?: string[];
}

export interface ExportIdentityCorrection {
  gender: string;
  birthdate: string;
  nationality: string; // ISO alpha-3; conversion to IOC happens only in the export.
}

export function validateIdentityCorrection(value: ExportIdentityCorrection): ExportIdentityCorrection {
  const correction = { gender: exportGender(value.gender), birthdate: exportBirthdate(value.birthdate), nationality: value.nationality.trim().toUpperCase() };
  if (!correction.gender || !correction.birthdate || !isoToIoc(correction.nationality)) {
    throw new Error('Fyll inn gyldig kjønn, full fødselsdato og nasjonalitet (ISO-landkode).');
  }
  if (correction.birthdate > new Date().toISOString().slice(0, 10)) throw new Error('Fødselsdato kan ikke være i fremtiden.');
  return correction;
}

export function correctExportRow(row: ExportResultRow, input: ExportIdentityCorrection): ExportResultRow {
  const value = validateIdentityCorrection(input);
  return { ...row, gender: value.gender, birthdate: value.birthdate, nationality: isoToIoc(value.nationality),
    issues: row.issues.filter(issue => !(row.identityIssues || []).includes(issue)), identityIssues: [] };
}

export const RESULT_EXPORT_HEADERS = [
  'Ranking', 'Time', 'Last Name', 'First Name', 'Birthdate', 'Gender', 'Nationality', 'Bib Number'
];

export function exportStatus(value?: string): string {
  const status = (value || '').trim().toLowerCase();
  if (['finished', 'finish', 'complete', 'completed'].includes(status)) return 'Finished';
  if (['dnf', 'dns', 'dsq', 'dq'].includes(status)) return status === 'dq' ? 'DSQ' : status.toUpperCase();
  return '';
}

export function exportBirthdate(value: unknown): string {
  if (!value) return '';
  // Timestamp birthdays are stored at UTC midnight. Never shift them to browser time.
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    value = (value as { toDate: () => Date }).toDate();
  }
  const raw = value instanceof Date ? (Number.isFinite(value.getTime()) ? value.toISOString().slice(0, 10) : '') : String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const date = new Date(`${raw}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === raw ? raw : '';
}

export function exportGender(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (['m', 'male', 'mann', 'man'].includes(raw)) return 'M';
  if (['f', 'female', 'k', 'kvinne', 'woman'].includes(raw)) return 'F';
  return '';
}

/** Multiple legacy/account documents may legitimately share a personId. Merge only
 * consistent identity fields; never choose an arbitrary document or guess a DOB. */
export function resolveExportIdentity(profiles: ExportIdentity[], registrations: ExportIdentity[]): ExportIdentity {
  const errors: string[] = [];
  const resolve = (field: keyof ExportIdentity, normalize: (value: unknown) => string): string => {
    const registered = Array.from(new Set(registrations.map(row => normalize(row[field])).filter(Boolean)));
    const values = registered.length ? registered : Array.from(new Set(profiles.map(row => normalize(row[field])).filter(Boolean)));
    if (values.length > 1) errors.push(`Motstridende ${field} for person-ID`);
    return values.length === 1 ? values[0] : '';
  };
  const result: ExportIdentity = {
    gender: resolve('gender', exportGender),
    dateOfBirth: resolve('dateOfBirth', exportBirthdate),
    nationality: resolve('nationality', value => String(value || '').trim().toUpperCase())
  };
  if (!profiles.length && !registrations.length) errors.push('Fant ikke personprofil');
  if (errors.length) result.error = errors.join('; ');
  return result;
}

export function exportRaceRows(results: KUTCResultEntry[], identities: Record<string, ExportIdentity>): ExportResultRow[] {
  const seen = new Set<number>();
  return results.map(result => {
    const identity = identities[String(result.personId)] || {};
    const status = exportStatus(result.status);
    const seconds = result.raceTimeSeconds;
    const time = typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
      ? `${Math.floor(seconds / 3600).toString().padStart(2, '0')}:${Math.floor(seconds % 3600 / 60).toString().padStart(2, '0')}:${Math.floor(seconds % 60).toString().padStart(2, '0')}` : '';
    const row: ExportResultRow = {
      personId: result.personId,
      rank: status === 'Finished' ? (typeof result.raceRank === 'number' && Number.isInteger(result.raceRank) && result.raceRank > 0 ? result.raceRank : '')
        : status === 'DNF' || status === 'DNS' || status === 'DSQ' ? status : '',
      time: status === 'Finished' ? time : '',
      firstName: result.firstName?.trim() || '',
      lastName: result.lastName?.trim() || '',
      gender: exportGender(identity.gender),
      birthdate: exportBirthdate(identity.dateOfBirth),
      nationality: isoToIoc(identity.nationality),
      bib: String(result.bib ?? '').trim(), status, issues: []
    };
    if (!Number.isInteger(row.personId) || row.personId <= 0) row.issues.push('Ugyldig person-ID');
    if (seen.has(row.personId)) row.issues.push('Duplikat person-ID i distansen');
    seen.add(row.personId);
    const identityIssues: string[] = [];
    if (identity.error) identityIssues.push(identity.error);
    if (!row.firstName || !row.lastName) row.issues.push('Mangler navn');
    if (!row.gender) identityIssues.push('Mangler eller ukjent kjønn');
    if (!row.birthdate) identityIssues.push('Mangler gyldig fødselsdato');
    if (!row.nationality) identityIssues.push('Mangler nasjonalitet som kan konverteres til IOC-landkode');
    row.identityIssues = identityIssues;
    row.issues.push(...identityIssues);
    if (!row.status) row.issues.push(`Ukjent resultatstatus: ${result.status || '(tom)'}`);
    if (status === 'DSQ') row.issues.push('DSQ må avklares med UTMB før eksport');
    if (status === 'Finished' && (!row.rank || !row.time)) row.issues.push('Fullført uten gyldig distanseplassering/tid');
    return row;
  }).sort((a, b) => {
    const rankA = typeof a.rank === 'number' ? a.rank : Number.MAX_SAFE_INTEGER;
    const rankB = typeof b.rank === 'number' ? b.rank : Number.MAX_SAFE_INTEGER;
    return rankA - rankB || a.bib.localeCompare(b.bib, undefined, { numeric: true });
  });
}

export function utmbExportRows(rows: ExportResultRow[]): ExportResultRow[] {
  const seconds = (time: string): number => time.split(':').reduce((total, value) => total * 60 + Number(value), 0);
  return rows.filter(row => row.status !== 'DNS').slice().sort((a, b) => {
    if (a.status === 'Finished' && b.status !== 'Finished') return -1;
    if (a.status !== 'Finished' && b.status === 'Finished') return 1;
    if (a.status === 'Finished' && b.status === 'Finished') return seconds(a.time) - seconds(b.time) || Number(a.rank) - Number(b.rank);
    return a.bib.localeCompare(b.bib, undefined, { numeric: true });
  });
}

export function resultExportData(editionId: string, distanceKey: string, rows: ExportResultRow[]): (string | number)[][] {
  if (!/^kutc-\d{4}$/.test(editionId) || !distanceKey || distanceKey.trim().toLowerCase() === 'total' || distanceKey.includes('/')) {
    throw new Error('Velg en KUTC-årsutgave og en distanse. Totalkonkurransen kan ikke eksporteres.');
  }
  rows = utmbExportRows(rows);
  if (!rows.length) throw new Error('Ingen resultater å eksportere.');
  if (rows.some(row => row.issues.length)) throw new Error('Rett manglende eller ugyldige opplysninger før eksport.');
  const finished = rows.filter(row => row.status === 'Finished');
  if (finished.some((row, index) => (index === 0 && row.rank !== 1) || (index > 0 && (
    Number(row.rank) < Number(finished[index - 1].rank) ||
    (row.rank === finished[index - 1].rank && row.time !== finished[index - 1].time)
  )))) {
    throw new Error('Distanseplasseringene må være én samlet rangering fra raskest til tregest.');
  }
  if (rows.some(row => row.status !== 'Finished' && row.status !== 'DNF')) throw new Error('UTMB-eksport støtter fullførte og DNF.');
  return [RESULT_EXPORT_HEADERS, ...rows.map(row => [row.rank, row.time, row.lastName, row.firstName, row.birthdate, row.gender, row.nationality, row.bib])];
}
